//#region imports
import * as angular from 'angular';
import { fn, INamed, IInfOperation } from '@common/utils';
import UISafe = fn.UISafe;
import resolveToError = fn.resolveToError;
import { constants as kconstants, AsObservableArray, IInfNotification } from '@common/navkendo';
import * as kutils from '../navkendo';
import { GetGridOptionSettings, INavGrid, INavGridImp, NavGridOptions } from '@common/navkendo/navgrid';
import { NavDock } from '../navkendo/navdock';
import { IPageMetaData, IPageQueryConstraint, } from '../nav';
import { NavClientService, INavPageClientInfo } from '../navdata/navdata';
import { NavSettingsService, SettingsContainer, CreateSetting } from '@common/services/settingsservice';
import { DialogService } from '@services/dialogservice';
import { SearchInputs } from '../navkendo';
import { GetCustomKendoTemplate } from '../kendo/templates';
import { KWFBenutzerSchreibtisch } from './userdesk';
import { CompatibilityService, IDeferred } from '@services/compatibilityservice';
import { IsKameral, RwfBootstrap, AccountingMode, IFDRW_Workflow_Invoice_Ledger, Pages } from '@common/rwf';
import { TSearchPage, RwfAppSettings } from '@common/rwf/rwf.appsettings';
import { OpenDocInfo } from '../CtrWFInstanceAddOnData';
import { OpenRowItem } from '../KWFUtils';
//#endregion

//#region Notifications

/** Notification about a Change of Current Search [KWFSearchSettingsService] */
export const $KWFCurrentSearchChanged = "$KWFCurrentSearchChanged";
/** Notification about Request to Change the Current Search */
export const $KWFCurrentSearchSet = "$KWFCurrentSearchSet";

export const NavSearch = "NavSearch";
//#endregion

export class SearchOption {
  constructor(initChecked?: boolean) {
    this.Checked = !!initChecked;
  }

  /** Option is currently Checked */
  Checked: boolean;
}

/** Search Options (i.e. Entities to Search in)*/
export class SearchOptions {
  Inbox: SearchOption = new SearchOption(true);
  Outbox: SearchOption = new SearchOption(true);
  BWF: SearchOption = new SearchOption(false);
  Pending: SearchOption = new SearchOption(false);
  Completed: SearchOption = new SearchOption(true);

  /** Marker Search is Currently Pending */
  SearchPending: boolean = false;

  /** How often was a Search Started so far?*/
  StartExecutionCounter: number = 0;

  /** Calculated Search Pages from Selected Options [the NAV Names!] */
  NavSearchPages: string[];

  /** Paging size of all grids */
  PagingSize: number = 20;

  Settings() {
    return CreateSetting(() => {
      return {
        PagingSize: this.PagingSize,
        InboxChecked: this.Inbox.Checked,
        OutboxChecked: this.Outbox.Checked,
        BWFChecked: this.BWF.Checked,
        PendingChecked: this.Pending.Checked,
        CompletedChecked: this.Completed.Checked,
      };
    }, state => {
      this.PagingSize = state.PagingSize;
      this.Inbox.Checked = state.InboxChecked;
      this.Outbox.Checked = state.OutboxChecked;
      this.BWF.Checked = state.BWFChecked;
      this.Pending.Checked = state.PendingChecked;
      this.Completed.Checked = state.CompletedChecked;
    });
  }
}

/** Object Representing NAV Page Search Data */
export class NavPageSearch {
  constructor(
    /** Name : "RunningInvoiceInbox", "RunningInvoiceOutbox", "ArchivedInvoiceInbox", or "ArchivedInvoiceOutbox" */
    public Name: TSearchPage,
    /** Nav Page Name for search */
    public NavPageName: string,
    /** Title for Search */
    public PageTitle: string,
    /** array of searchOptions to be check for this Search to be active */
    public SearchOptions: SearchOption[],
    /** Bav Url */
    private NavUrl: string,
    /** ´Nav Client Service ["ReadMultiple"] */
    private NavClient: NavClientService,
    private Constraints: () => IPageQueryConstraint[],
    private ResolveGrid: () => INavGrid
  ) { }
  private data = AsObservableArray([]);
  private dataSource: kendo.data.DataSource;

  /** Get Grid (if already instantiated) */
  public GetGrid() { return this.ResolveGrid(); }

  public PageClient: INavPageClientInfo;

  /** Create Options for Grid Display */
  public CreateGridOptions(preColumns?: kendo.ui.GridColumn[]) {
    return this.NavClient.GetPageClientInfo(this.NavPageName, this.NavUrl).then((pageClient) => {
      this.PageClient = pageClient;
      var metaData = pageClient.MetaData;
      // prepare Columns
      var kGridColumns = metaData.Properties.filter(item => item.Invisible !== true).map(p => metaData.GetColumn(p));
      if (preColumns)
        kGridColumns.unshift(...preColumns);
      var dataSource: kendo.data.DataSource;
      var result: NavGridOptions = {
        columns: kGridColumns,
        dataSource: this.dataSource = dataSource = kutils.GetGridDataSource(this.data, metaData.Model),
        selectable: true, // "multiple" -> no text selection!
        resizable: true,
        reorderable: true,
        sortable: {},
        groupable: { messages: { empty: "Ziehen Sie eine Spaltenüberschrift hierher, um Belege nach dieser Spalte zu gruppieren!" } },
        pageable: {
          pageSize: kconstants.DefaultPaging,
          pageSizes: kconstants.DefaultPagings,
          messages: {
            display: "Anzeigen der Belege {0} - {1} von {2}",
            empty: "Keine Belege zum Anzeigen",
            itemsPerPage: "Belege pro Seite"
          }
        },
        columnMenu: <any>true,
        //toolbar: ["excel"],
        excel: {
          allPages: true
        },
        navigatable: true,
        groupingDescription: "Belege gruppiert nach:"
      };
      kutils.UpdateGridOptions(result);

      // Problem: there is no "Sort" event on grid, but we want to store settings on changed sort order
      // Explicitly 'overwrite' sort event and trigger if desired
      var bakSortFunc = dataSource.sort;
      dataSource.sort = (...args) => {
        const result = bakSortFunc.call(dataSource, ...args);
        if (args.length == 1 && args[0] != undefined) {
          var grid = this.GetGrid();
          if (grid) {
            grid.trigger("customColumnChange");
          }
        }
        return result;
      };
      return result;
    });
  }

  /** Start a New search */
  public Search(constraints?: IPageQueryConstraint[]) {
    this.Reset();

    var allConstraints = new Array<IPageQueryConstraint>();
    allConstraints.push(...(constraints || []));
    allConstraints.push(...((this.Constraints && this.Constraints()) || []));

    var result = this.currSearch = this.NavClient.ReadMultiple({
      Constraints: allConstraints, PageName: this.NavPageName, PageCount: this.MaxCount
    }, this.NavUrl).then(data => {
      if (this.currSearch == result) {
        this.InProgress = false;
        this.data.push(...data.Data);
        var bookmark = data.Bookmark;
        if (bookmark) {
          this.CountInfo = (!bookmark.AllRead ? ">" : "") + bookmark.TotalReadCount;
        }
      }
      return data;
    });

    const doReset = () => {
      if (this.currSearch == result) {
        this.InProgress = false;
        this.currSearch = null;
      }
    };
    result.then(doReset, doReset);
    this.InProgress = true;

    return result;
  }
  private currSearch: PromiseLike<any>;

  /** Reset State, Cancel search if Running */
  public Reset() {
    this.data.splice(0, this.data.length);
    if (this.dataSource)
      this.dataSource.page(0);
    this.InProgress = false;
    this.currSearch = null;
    this.CountInfo = "";
  }

  /** Search is Currently In Progress */
  public InProgress = false;
  /** ShortInfo about search Result Count ('0', '3', '>1000') */
  public CountInfo: string;
  /** Marker : Search is currently Active */
  public IsActive = false;
  /** Mx Search Items to fetch */
  public MaxCount = 1000;
}

/** Search controller */
export class KWFSearchCtrl {
  static Directive = [() => <ng.IDirective>{
    template: require('./kwfsearch.fragment.html'),
    controller: KWFSearchCtrl,
    controllerAs: "vm"
  }];
  static $inject = ["$scope", "CompatibilityService", "RwfAppSettings", "$timeout", "KWFBenutzerSchreibtisch", "KWFSearchSettings", "KWFNotificationService"];
  constructor($scope: ng.IScope,
    $c: CompatibilityService,
    RwfAppSettings: RwfAppSettings,
    $timeout: ng.ITimeoutService,
    KWFBenutzerSchreibtisch: KWFBenutzerSchreibtisch,
    KWFSearchSettings: KWFSearchSettingsService,
    KWFNotificationService: IInfNotification
  ) {
    const RwfSetup = KWFBenutzerSchreibtisch.WfSetup,
      RwfIsKameral = this.IsKameral = IsKameral(RwfSetup),
      DialogService = KWFBenutzerSchreibtisch.DialogService,
      appSettings = RwfAppSettings.LoadedAppSettings,
      searchPages = RwfIsKameral ? appSettings.SearchPages_KAM : appSettings.SearchPages,
      NavClient = KWFBenutzerSchreibtisch.NavClient,
      navUrl = KWFBenutzerSchreibtisch.ApplicationSettings.NavUrl;

    this.IsBWFActive = RwfSetup.IsBWFActive;

    var searchOptions = $scope["SearchOptions"] = new SearchOptions();
    // Setup Configuration per Search
    var navSearches = $scope[NavSearch] = CreateSearchPageConfigs();
    var c = appSettings.CustomCaptions;
    $scope["SearchCaptions"] = $.extend({ Eingang: "Eingang", Ausgang: "Ausgang", Auftrag: "Auftrag", Order: "Bestellung" },
      (c && (RwfIsKameral ? c.Search_Kam : c.Search)));

    let searchInputs = $scope["SearchInputs"] = new SearchInputs();
    // Set Current Search when requested by event
    $scope.$on("$destroy", <any>$scope.$on($KWFCurrentSearchSet, (e, searchName) => {
      KWFSearchSettings.ApplyItem(searchName);
    }));

    var currentSearchPages: INamed<NavPageSearch> = {};
    // React on any Checked State Change
    var unbind = $scope.$watchGroup([() => searchOptions.Inbox.Checked, () => searchOptions.Outbox.Checked,
    () => searchOptions.Pending.Checked, () => searchOptions.Completed.Checked, () => searchOptions.BWF.Checked], (newVal, oldVal) => {
      // Refresh the Currently available Search Configurations
      docTypeChecked.CreditMemo.Disabled = docTypeChecked.Invoice.Disabled = (searchOptions.Inbox.Checked === searchOptions.Outbox.Checked) && (searchOptions.Outbox.Checked === false)
      var newActivePages = new Array<NavPageSearch>();
      angular.forEach(navSearches, (value: NavPageSearch, key: string) => {
        if (value) {
          var active = value.SearchOptions.every(item => item.Checked);
          if (active) {
            if (!value.IsActive) {
              newActivePages.push(value);
            }
          }
          value.IsActive = active;
        }
      });
      var activeSearches = fn.DictToArray(navSearches).filter(item => item.IsActive);
      currentSearchPages = $scope["CurrentSearchPages"] = fn.ToDictionary(activeSearches, "Name");
      searchOptions.NavSearchPages = activeSearches.map(item => item.NavPageName);

      // If user provided constraints, a newly activated Page will automatically be triggered to search
      // if a Search has ever been triggered so far
      if (newActivePages.length > 0 && searchOptions.StartExecutionCounter > 0) {
        if (searchInputs.GetAsConstraints().length > 0) {
          // Execute deferred : otherwise State will not yet be attached on first visualization
          $c.timeout().then(() => StartExecuteSearch(newActivePages));
        }
      }

      kutils.EnsureCheckedTab($("#srtabstrip").data("kendoTabStrip"));
    });

    /** Operation : Export Search Results to Excel */
    var actExcelExport = $scope["actExcelExport"] = <IInfOperation>{ Execute: StartExcelExport };
    $scope.$watch(() => searchOptions.SearchPending, () => actExcelExport.Enabled = !searchOptions.SearchPending);
    // triggered from search input directive
    $scope["StartExecuteSearch"] = StartExecuteSearch;

    const docTypeChecked = $scope["DocTypeChecked"] = {
      /** Checkbox 'Rechnung' */
      Invoice: { Checked: true, Disabled: false },
      /** Checkbox 'Gutschrift' */
      CreditMemo: { Checked: true, Disabled: false }
    };

    // Is dataItem present in myInvoice?
    var isEditable = (dataItem) => {
      const id = dataItem?.ID;
      return id && !!fn.FindFirst(KWFBenutzerSchreibtisch.InvoiceData?.MyInvoices?.Items, item => fn.GuidEqual(item.ID, id))
    };

    //#region Pre columns
    //var templateStatusOK = '<span class="idms_Image idms_Image_OK"></span>'; //'<span class="idms_Icon idms_GridIcon idms_Icon_OK idms_Color_OK"></span>';
    //var templateStatusWarning = '<span class="idms_Image idms_Image_Warning"></span>';
    //var templateStatusError = '<span class="idms_Image idms_Image_Error"></span>';
    //var statusTemplate = templateStatusOK;
    // TODO: Bind status template to real status column
    var colPreview =
      // Preview Document Column
      <kendo.ui.GridColumn>{
        resizable: false, reorderable: true, sortable: true, groupable: true, columnMenu: true, width: kconstants.KendoGrid_PreviewIconColumnSize,
        template: GetCustomKendoTemplate("DataGridNavSearchPreviewButtonTemplate")
      };
    var defaultNonRunningAction: (event) => void;
    // KWF3
    var colEdit = <kendo.ui.GridColumn>{
      field: '',
      filterable: false,
      title: '',
      width: kconstants.KendoGrid_ActionIconColumnSize,
      template: (function (dataItem) {
        var showEditIcon = isEditable(dataItem);
        return showEditIcon ?
          "<span class='idms_Icon idms_Icon_Edit idms_HoverIcon' title='Beleg bearbeiten' onclick='KWF.EditWorkflowInstance(this, event)'></span>" : "";
      })
    };
    // Readonly button
    var readonlyCol = <kendo.ui.GridColumn>{
      field: '',
      filterable: false,
      title: '',
      width: kconstants.KendoGrid_ActionLargerIconColumnSize,
      template: (function (dataItem) {
        return "<span class='idms_Icon idms_Icon_LEye idms_HoverIcon' title='Beleg lesend öffnen' onclick='KWF.EditWorkflowInstance(this, event, true)'></span>";
      })
    };
    // Show Belegauskunft for archived docs in Nav mode only
    var colInfo = <kendo.ui.GridColumn>{
      title: "", width: kconstants.KendoGrid_ActionIconColumnSize, filterable: false,
      command: [{
        attr: { title: "Belegauskunft anzeigen" },
        name: "openJournal", text: "", className: "idms_DataGridButton",
        click: defaultNonRunningAction = (event) => OpenDocInfo(event),
        template: "<a class='k-grid-openJournal'><span class='idms_Image16 idms_Image_Info16 idms_HoverImage' title='Belegauskunft anzeigen'></span></a>"
      }]
    };

    var searchGridOptions = <INamed<NavGridOptions>>{};
    fn.Remap(navSearches, (s, name) => s.CreateGridOptions(name.indexOf("Running") == 0 ? [colEdit, readonlyCol, colPreview] : [colPreview, colInfo]).then((gOptions) => {
      searchGridOptions[name] = gOptions;
      if (name.indexOf("Running") == 0) {
        gOptions.dblClick = (rowitem) =>
          isEditable(rowitem.item) && OpenRowItem($c, rowitem.item);
      } else {
        gOptions.dblClick = (e) => defaultNonRunningAction(e.event);
      }
    }));

    //#endregion

    function autoToggle(...items: { Checked: boolean }[]) {
      (items = items.filter(fn.isSet)).forEach(kind => $scope.$watch(() => kind.Checked, (newValue, oldValue) => {
        if (!newValue && (newValue !== oldValue)) {
          var others = items.filter(item => item !== kind);
          if (items.length == 2) {
            // toggle other
            others.forEach(item => item.Checked = true);
          }
          else if (items.length > 2) {
            // Not allowed to untoggle all
            if (others.every(item => !item.Checked))
              kind.Checked = true;
          }
        }
      }));
    }
    autoToggle(searchOptions.Inbox, searchOptions.Outbox, (navSearches.ArchivedBWF || navSearches.RunningBWF) && searchOptions.BWF);
    autoToggle(searchOptions.Pending, searchOptions.Completed);
    autoToggle(docTypeChecked.Invoice, docTypeChecked.CreditMemo);
    let notificationGroup = ".search.",
      getNotification = () => <IInfNotification>$scope["searchErrors"] || KWFNotificationService;

    var showWarning = function (title: string, msg: string) {
      getNotification().AddNotification({ title: title, message: msg, messageType: "warning", group: notificationGroup });
    };

    var showError = function (title: string, msg: string) {
      getNotification().AddNotification({ title: title, message: msg, messageType: "error", group: notificationGroup });
    };

    var clearNotifications = () => {
      getNotification().RemoveNotifications(notificationGroup);
    };
    // Clear search Related Notifications on leave view, Selected Menu Item Change
    $scope.$on("$destroy", clearNotifications);
    $scope.$watch("MainMenu.SelectedItem", clearNotifications);

    /** Setup User Settings Persistence Functionality */
    function settingsAccess() {
      var result = CreateSetting(() => {
        return {
          InvoiceChecked: docTypeChecked.Invoice.Checked,
          CreditMemoChecked: docTypeChecked.CreditMemo.Checked
        };
      }, state => {
        docTypeChecked.CreditMemo.Checked = state.CreditMemoChecked;
        docTypeChecked.Invoice.Checked = state.InvoiceChecked;
      });
      result.addSubItem("SearchOptions", () => searchOptions.Settings());
      result.addSubItem("SearchInput", () => searchInputs.Settings());

      fn.Remap(navSearches, navsearch => {
        result.addSubItem("SearchGrid_" + navsearch.Name, () => GetGridOptionSettings(searchGridOptions[navsearch.Name], () => navsearch.GetGrid()));
      });
      return result;
    };

    /**  last started, not cancelled search*/
    var lastStartedSearch: IDeferred<boolean>;
    /** function to Start a new Search on either all currently active Search Pages (pageNames not Set, or explicitly given PageNames) */
    function StartExecuteSearch(searches?: NavPageSearch[]) {
      // Show search result panel after first search
      if ($('#searchResults').css('display') === 'none') $('#searchResults').fadeIn();
      // Reset alerts
      clearNotifications();

      var result = lastStartedSearch = $c.defer<boolean>();
      $c.finally(result.promise, () => {
        if (result == lastStartedSearch) {
          searchOptions.SearchPending = false;
        };
        if (initDebounce) {
          initDebounce();
        }
      });
      try {
        var startedSearchPromises = (searches || fn.DictToArray(currentSearchPages)).filter(fn.isSet)
          .map(item => {
            return NavClient.GetPageClientInfo(item.NavPageName, navUrl).then(() => item.Search().then(r => {
              $scope.$emit("kwfUserDesk.oneSearchEnd");
              if (result == lastStartedSearch) {
                if (r.Data.length >= item.MaxCount && !r.Bookmark.AllRead) {
                  showWarning('Es wurden über ' + item.MaxCount + ' Treffer in "' + item.PageTitle + '" gefunden!',
                    "Nur die ersten " + item.MaxCount + " Treffer werden aus Performancegründen angezeigt und können nach Excel exportiert werden. " +
                    "Nutzen Sie für umfangreichere Suchen den NAV-Client oder schränken Sie Ihre Suche weiter ein!");
                }
              }
            })).then(undefined, err => {
              showError("Suche in '" + item.PageTitle + "' fehlgeschlagen", resolveToError(err, false));
            });
          });
        // Attach Grid Options after start of Search (perf Opt)
        if (!$scope["SearchGridOptions"])
          window.setTimeout(() => $scope["SearchGridOptions"] = searchGridOptions);

        if (startedSearchPromises.length == 0) {
          // no search neccessary
          result.resolve(false);
        } else {
          // start searches
          searchOptions.SearchPending = true;
          searchOptions.StartExecutionCounter += 1;
          $c.finally($c.all(startedSearchPromises), () => {
            result.resolve(true);

            $scope.$emit("kwfUserDesk.allSearchEnd");
          });
        }
      } catch (err) {
        showWarning("Die Suche konnte nicht gestartet werden!", resolveToError(err, false));
        result.reject(err);
      }
      return result.promise;
    };

    function CancelSearches() {
      fn.DictToArray(currentSearchPages).forEach(item => item && item.Reset());
      lastStartedSearch = null;
      searchOptions.SearchPending = false;
    };

    function StartExcelExport() {
      RwfAppSettings.EffectiveSearchPages.then((effectiveSearchPages) => {
        var grids = Object.getOwnPropertyNames(navSearches).filter(item => !!currentSearchPages[item]).map(pageName => { return { Grid: navSearches[pageName].GetGrid(), Name: effectiveSearchPages[pageName].PageTitle ? effectiveSearchPages[pageName].PageTitle : pageName, PageName: pageName }; }).filter(item => !!item.Grid);
        if (grids.length == 0)
          throw Error("Keine Ansicht zum Export gefunden!");
        if (grids.filter(item => !item.Grid).length > 0)
          throw new Error("Grid- Ansicht(en) konnte nicht bestimmt werden!");

        kutils.SaveAsExcel(grids);
      });
    }

    /** Create Configuration : which pages must be searched per Search Option ? */
    function CreateSearchPageConfigs(this: void) {
      var perPageOptions: { [name in TSearchPage]: SearchOption[] } = {
        RunningInvoiceInbox: [searchOptions.Inbox, searchOptions.Pending],
        RunningInvoiceOutbox: [searchOptions.Outbox, searchOptions.Pending],
        ArchivedInvoiceInbox: [searchOptions.Inbox, searchOptions.Completed],
        ArchivedInvoiceOutbox: [searchOptions.Outbox, searchOptions.Completed],
        RunningInvoiceInOut: [searchOptions.Pending],
        ArchivedBWF: [searchOptions.BWF, searchOptions.Completed],
        RunningBWF: [searchOptions.BWF, searchOptions.Pending],
      };
      var result = fn.Remap(perPageOptions, (options, searchName: TSearchPage) => {
        var pageInfo = searchPages[searchName];

        if (!pageInfo || pageInfo.Disabled || !pageInfo.NavPageName)
          return;

        // BWF Pages only if "ISBWFActive"
        const bwfPages: TSearchPage[] = ["ArchivedBWF", "RunningBWF"];
        if (!RwfSetup.IsBWFActive && bwfPages.indexOf(searchName) >= 0)
          return;

        if (searchName === "RunningInvoiceInOut" && !IsKameral(RwfSetup)) {
          return;
        }

        var result: NavPageSearch = new NavPageSearch(searchName, pageInfo.NavPageName,
          pageInfo.PageTitle, options, navUrl, NavClient,
          () => getAllInputConstraints(searchName, pageInfo.QueryConstraints, result.PageClient.MetaData),
          () => $scope["grd" + searchName] // Convention: grid Representations Names : 'grd' + [SearchName] --> kwfSeach.fragment.html
        );

        return result;
      });
      // Remove "Undefined" Props
      fn.Remap(result, (r, name) => { if (!r) { delete result[name]; } });
      return result;
    }

    /** collection input constraint from User Input + custom Filters */
    function getAllInputConstraints(searchName: TSearchPage, constraints: IPageQueryConstraint[], metadata: IPageMetaData): IPageQueryConstraint[] {
      var result = searchInputs.GetAsConstraints(metadata);

      // Check for "Document_Type" field
      const pageName = metadata.PageName;

      // exclude "FWF" workflows so far
      if (metadata.PropsByName["WorkflowType"]) {
        result.unshift(<IPageQueryConstraint<IFDRW_Workflow_Invoice_Ledger>>{ FieldName: "WorkflowType", Value: "=''" });
      } else if (metadata.PropsByName["AccountingMode"]) {
        result.unshift({ FieldName: "AccountingMode", Value: "<>" + AccountingMode.FWF });
      }
      if (pageName == Pages.FDRW_Workflow_Invoice_Ledger.PageName) {
        if (!RwfIsKameral && RwfSetup.IsBWFActive) {
          result.unshift(<IPageQueryConstraint<IFDRW_Workflow_Invoice_Ledger>>{ FieldName: "DocumentType", Value: searchName == "RunningBWF" ? "@*bestellung*" : "<>@*bestellung*" });
        }
      }
      if ([Pages.FDRW_Workflow_Invoice_Ledger.PageName, Pages.FDRW_Workflow_User_Instances_List.PageName].indexOf(pageName) >= 0) {
        // "In Arbeit" : check if explicit filter necessary
        if (docTypeChecked.Invoice.Checked != docTypeChecked.CreditMemo.Checked && searchName != "RunningBWF") {
          result.unshift({ FieldName: "DocumentType", Value: docTypeChecked.CreditMemo.Checked ? "*gutschrift" : "*rechnung*" });
        }
      }
      else if (!metadata || metadata.PropsByName["Document_Type"]) {
        // Gebuchte Belege
        // 2 : Rechnung, 3 : Gutschrift
        var value = docTypeChecked.Invoice.Checked ? ["2"] : [];
        if (docTypeChecked.CreditMemo.Checked) value.push("3");
        result.unshift({ FieldName: "Document_Type", Value: value.join(",") });
      }
      // add externally configured constraints
      if (constraints)
        result.unshift(...constraints);
      return result;
    }

    /** Prompt for Search Config Name from given Visual State and add/save it */
    $scope["AddItemPrompt"] = (search?: SearchConfig) => {
      DialogService.ShowInput({
        title: "Suche speichern",
        message: "Bitte geben Sie den Namen ein, unter dem<br/>die aktuelle Suche gespeichert werden soll!",
        messageIsHtml: true,
        inputText: search ? search.Name : null
      }).then((result) => {
        if (result.okPressed) {
          var newSearchConfig = { Name: result.inputString, Settings: settingsAccess().getState() };
          KWFSearchSettings.ContainsItem(newSearchConfig.Name).then((alreadyExists) => {
            // Update item?
            if (alreadyExists) {
              DialogService.ShowConfirm({
                title: "Überschreiben?",
                message: "Es existiert bereits eine Suche mit dem Namen<br/><b>" + $("<span>").text(result.inputString).html() + "</b>.<br/>Möchten Sie diese überschreiben?",
                messageIsHtml: true
              }).then((overwriteSearch) => {
                if (overwriteSearch) {
                  KWFSearchSettings.AddItem(newSearchConfig);
                }
              });
            }
            // Add new item
            else {
              KWFSearchSettings.AddItem(newSearchConfig);
            }
          });
        }
      });
    };
    /** Reset the current search to the default setting */
    $scope["ResetCurrent"] = () => {
      UISafe(() => {
        CancelSearches();
        KWFSearchSettings.CurrentSearch = null;
        searchInputs.forEach(input => {
          input.QueryString = "";
          //input.SelectedProperty = undefined;
        });
      });
    };

    /** Search Config to Reset to Initial State (If Set : Initialization has finished) */
    var waitInit = $c.defer();
    // Store Initial Search State after Full Load of Application
    $scope.$watch(() => searchOptions.NavSearchPages, pages => {
      if (pages && pages.length) {
        const doResolve = () => { waitInit.resolve(); };
        searchInputs.RefreshAvailableProperties({
          NavClient: NavClient,
          Pages: pages,
          NavUrl: navUrl,
          Include: appSettings.SearchFields
        }).then(doResolve, doResolve);
      }
    });

    $c.finally(waitInit.promise.then(() => {
      // Apply Default item if no item explicitly set
      return KWFSearchSettings.GetUserSearchSettings().then(() => {
        return KWFSearchSettings.ApplyItem(KWFSearchSettings.CurrentSearch || KWFSearchSettings.InitialSearch || "", true);
      });
    }), () => {
      if (searchInputs.length == 0)
        searchInputs.AppendSearchInput();
      $scope["SearchInputLoaded"] = true;
    }); //
    /** Store Current search As Default */
    var storeCurrentAsDefault = () => KWFSearchSettings.SetDefault(KWFSearchSettings.CurrentSearch = { Name: "", Settings: settingsAccess().getState() });
    var debounceStore = fn.debounce(storeCurrentAsDefault, 250),
      /** initDebounce Store Settings (once grids are loaded)*/
      initDebounce = fn.once(() => {
        fn.DictToArray(navSearches).forEach(search => {
          var kgrid = (search && search.GetGrid());
          if (kgrid) {
            INavGridImp.ColumnChangeEvents.forEach(ev => kgrid.bind(ev, debounceStore));
          }
        });
      });
    $scope.$on("$destroy", storeCurrentAsDefault);

    $scope.$on("$destroy", <any>$scope.$on($KWFCurrentSearchChanged, (e) => {
      CancelSearches();
      var newSearch = KWFSearchSettings.CurrentSearch;
      if (newSearch && newSearch.Settings) {
        settingsAccess().apply(newSearch.Settings);
        // Update SearchTitle in Search expander
        var searchExpander: NavDock = $scope["userdesk_searchexpander"];
        if (searchExpander)
          searchExpander.SetTitle(newSearch.Name ? "Suche: " + newSearch.Name : "Suche");
        if (newSearch.Name) { // Do Not autotrigger for Default search
          // we must let the UI Refresh to Ensure the Configurations get applied
          // (otherwise the Search might get Execute with the wrong context)
          // (problem : We query state directly from UI, not from the model ...)
          $timeout(() => {
            if (searchInputs.GetAsConstraints().length > 0)
              StartExecuteSearch();
          });
        }
      }
    }));
  }

  public readonly IsBWFActive: boolean;

  public readonly IsKameral: boolean;
}

/** Service for Persistence and Applying of Users Search Configurations */
export class KWFSearchSettingsService {
  static $inject = ["CompatibilityService", "DialogService", "NavSettings", "$rootScope", "RwfBootstrap"];
  constructor($q: CompatibilityService, private DialogService: DialogService, NavSettings: NavSettingsService,
    private $rootScope: ng.IRootScopeService, rwfBootstrap: RwfBootstrap) {
    // We must 'discern' Storage of Search Configuration for Kameral / Doppisch (Different Pages ...)
    const resolveSearchName = rwfBootstrap.Resolve.then(s => IsKameral(s.RwfSetup) ? "UserSearches_KAM" : "UserSearches");
    this.ensureConfig = resolveSearchName.then((searchName) => {
      return new SettingsContainer<SearchConfig>($q,
        NavSettings.GetUserSetting("KWF/" + searchName),
        item => item.Name,
        { Name: "", Settings: null });
    });
  }
  private ensureConfig: PromiseLike<SettingsContainer<SearchConfig>>;

  /** Helper Marker to Remember Initial Search to apply after Initialization */
  public InitialSearch = "";

  /** Currently Selected Search */
  public CurrentSearch: SearchConfig = null;

  /** Get change Aware Container of Current Search Configurations */
  public GetUserSearchSettings(): PromiseLike<SearchConfig[]> {
    return this.ensureConfig.then(userConfigItems => userConfigItems.LoadItems());
  }

  public ContainsItem(name: string): PromiseLike<boolean> {
    return this.ensureConfig.then(userConfigItems => userConfigItems.LoadItems(false).then((items) => {
      // Update item
      return items.filter(item => item.Name == name).length > 0;
    }));
  }

  /** Set Default Setting (No Name, Hidden for User Selectable Settings) */
  public SetDefault(item: SearchConfig) {
    item.Name = ""; // Default Marker
    this.ensureConfig.then((userConfigItems) => userConfigItems.LoadItems().then(items => {
      var existing = items.filter(item => !item.Name)[0];
      if (existing)
        userConfigItems.ReplaceItem(existing, item);
      else
        userConfigItems.AddItem(item);
    }));
  }

  /** Replace  full Container of Items */
  public SetItems(items: SearchConfig[]) {
    return this.ensureConfig.then(userItems => userItems.SetItems(items).then(() => userItems.LoadItems()));
  }

  /** Add Search Config by Name from given Visual State*/
  public AddItem(newSearch: SearchConfig) {
    return UISafe(() => {
      if (!newSearch || !newSearch.Name || !(typeof newSearch.Name == "string"))
        throw Error("Kein Name zum Anlegen einer Suchkonfiguration übergeben!");

      return UISafe(this.GetUserSearchSettings().then((items) => {
        return this.ContainsItem(newSearch.Name).then((alreadyExists) => {
          return this.ensureConfig.then(userConfigItems => {
            // Update item
            if (alreadyExists) {
              return userConfigItems.ReplaceItem(newSearch, newSearch);
            }
            // Add new item
            else {
              return userConfigItems.AddItem(newSearch);
            }
          });
        }).then(result => {
          this.CurrentSearch = result;
        });
      }));
    });
  }

  /** Delete Search Configuration Item*/
  public DeleteItemPrompt(item: SearchConfig) {
    if (item) {
      this.DialogService.ShowConfirm({
        title: "Suche löschen",
        message: "Wollen Sie die Suche <b>" + $('<span>').text(item.Name).html() + "</b> wirklich löschen?",
        messageIsHtml: true
      }).then((b) => {
        if (b) {
          UISafe(this.ensureConfig.then(userConfigItems => userConfigItems.RemoveItem(item).then(() => {
            if (item == this.CurrentSearch)
              this.CurrentSearch = null;
          })));
        }
      });
    }
  }

  public RenameItemPrompt(item: SearchConfig) {
    if (item) {
      this.DialogService.ShowInput({
        title: "Suche umbenennen",
        message: 'Bitte geben Sie den neuen Namen für die Suche<br/><b>' + $('<span>').text(item.Name).html() + '</b> ein!',
        inputText: item.Name,
        messageIsHtml: true
      }).then((result) => {
        if (result.okPressed && result.inputString && item.Name !== result.inputString) {
          this.ContainsItem(result.inputString).then((alreadyExists) => {
            // Name already exists
            if (alreadyExists) {
              this.DialogService.ShowDialog({
                title: "Umbenennen fehlgeschlagen",
                message: "Eine Suche mit dem gewählten Namen existiert bereits!"
              });
            }
            // Rename item
            else {
              var renamedConfig: SearchConfig = {
                Name: result.inputString,
                Settings: item.Settings
              };
              UISafe(this.ensureConfig.then(userConfigItems =>
                userConfigItems.ReplaceItem(item, renamedConfig).then((addeditem) => this.CurrentSearch = addeditem))
              );
            }
          });
        }
      });
    }
  }

  /** Apply Current Search Configuration Item */
  public ApplyItem(itemOrName: string | SearchConfig, force?: boolean): PromiseLike<void> | void {
    if (typeof itemOrName == "string") {
      return this.ensureConfig.then(userConfigItems => userConfigItems.LoadItems().then(settings => {
        var match = settings.filter(item => item.Name == itemOrName)[0];
        if (match) {
          return this.ApplyItem(match, force);
        }
      }));
    } else if (itemOrName) {
      if (force || (this.CurrentSearch !== itemOrName)) {
        this.CurrentSearch = <SearchConfig>itemOrName;
        this.$rootScope.$broadcast($KWFCurrentSearchChanged, itemOrName);
      }
    }
  }
}

/** Single Search Configuration Item, PERSISTED! */
export interface SearchConfig {
  /** Name of Search Config Item */
  Name: string;
  /** Settings of the Item */
  Settings?: any;
}
